import * as React from 'react';
import { useState } from 'react';
import {
  InviteTeamMemberModalView,
  InviteTeamMemberModalViewProps,
  SubmittedDataInviteTeamMemberFormType
} from './InviteTeamMemberModal.view';
import { RegisterOptions, useForm, UseFormReturn } from 'react-hook-form';
import { FormOptions, ValidationPatterns } from '../../../constants/form';
import { ValidationMessages } from '../../../constants/validation-messages';
import { ApolloError } from '@apollo/client';
import {
  CreateInvitedMemberMutationFn,
  GetOrganizationDocument,
  GetOrganizationQuery,
  GetOrganizationQueryVariables,
  PlanUsageType,
  UsageFieldsFragmentDoc,
  UserInvitationInput,
  UserOrganizationRole
} from '../../../models/graphql';
import { useUserRole } from '../../../hooks/useUserRole';
import { remapApolloErrors } from '../../../helpers/remapApolloErrors';
import { showToast } from '../../../helpers/showToast';

export type InviteTeamMemberModalControllerProps = Omit<
  InviteTeamMemberModalViewProps,
  'onSubmit' | 'submitted' | 'useFormInstance' | 'roleRules' | 'emailRules' | 'workspaceRules' | 'selectedOrgRole'
> & {
  createUserInvitation: CreateInvitedMemberMutationFn;
};

export const InviteTeamMemberModalController = (props: InviteTeamMemberModalControllerProps): JSX.Element => {
  const { selectedOrgRole } = useUserRole();
  const [submitted, setSubmitted] = useState<boolean>(false);
  const defaultFormValues: Record<string, string | boolean> = { email: '', role: UserOrganizationRole.EDITOR };

  props.organization.workspaces.forEach((workspace, index) => {
    defaultFormValues[`workspaces.${index}`] = workspace.id;
  });

  const useFormInstance: UseFormReturn = useForm({
    ...FormOptions,
    defaultValues: defaultFormValues
  });

  const onSubmit = async (data: SubmittedDataInviteTeamMemberFormType) => {
    setSubmitted(true);
    const assignedWorkspaces: string[] = [];

    if (props.organization.id) {
      if (data.role !== UserOrganizationRole.ADMIN && data.workspaces) {
        for (const value of Object.values(data.workspaces)) {
          if (value) {
            assignedWorkspaces.push(value);
          }
        }
      } else {
        // Assign all workspaces to an Admin role
        props.organization.workspaces.forEach(workspace => {
          assignedWorkspaces.push(workspace.id);
        });
      }

      const input: UserInvitationInput = {
        email: data.email,
        role: data.role,
        workspaceIds: assignedWorkspaces
      };

      await props
        .createUserInvitation({
          variables: {
            organizationId: props.organization.id,
            input: input
          },
          update: (cache, result) => {
            if (!props.organization || !result.data?.createUserInvitation) {
              return;
            }

            const data = cache.readQuery<GetOrganizationQuery, GetOrganizationQueryVariables>({
              query: GetOrganizationDocument,
              variables: { organizationId: props.organization.id }
            });

            if (!data || !data.organization) {
              return;
            }

            const newData: GetOrganizationQuery = {
              organization: {
                __typename: 'Organization',
                id: data.organization.id,
                userAssignments: data?.organization?.userAssignments || [],
                workspaces: data?.organization?.workspaces || [],
                userInvitations: [
                  ...(data?.organization?.userInvitations || []),
                  {
                    __typename: 'UserInvitation',
                    id: result.data.createUserInvitation.id,
                    email: result.data.createUserInvitation.email,
                    role: result.data.createUserInvitation.role,
                    workspaceIds: result.data.createUserInvitation.workspaceIds,
                    token: result.data.createUserInvitation.token
                  }
                ]
              }
            };

            cache.writeQuery({
              query: GetOrganizationDocument,
              variables: { organizationId: props.organization?.id },
              data: newData
            });

            const oldUsage: PlanUsageType | null = cache.readFragment({
              fragment: UsageFieldsFragmentDoc,
              fragmentName: 'UsageFields',
              id: 'PlanUsageType:{}'
            });
            const newUsage = { ...oldUsage, users: (oldUsage?.users ?? 0) + 1 };
            cache.writeFragment({
              fragment: UsageFieldsFragmentDoc,
              fragmentName: 'UsageFields',
              id: 'PlanUsageType:{}',
              data: newUsage
            });
          }
        })
        .then(() => {
          props.onClose();
        })
        .catch(e => {
          const errorForRemapping = e as ApolloError;
          const mappedError = remapApolloErrors(errorForRemapping);
          showToast({
            summary: 'Error in creating the invitation',
            detail: mappedError,
            severity: 'error'
          });
          setSubmitted(false);
        });
    }
  };

  const emailRules: RegisterOptions = {
    required: ValidationMessages.email.required,
    pattern: {
      value: ValidationPatterns.email,
      message: ValidationMessages.email.invalid
    }
  };

  const roleRules: RegisterOptions = {};
  const workspaceRules: RegisterOptions = {};

  const viewProps: InviteTeamMemberModalViewProps = {
    ...props,
    onSubmit,
    useFormInstance,
    submitted,
    roleRules,
    emailRules,
    workspaceRules,
    selectedOrgRole
  };
  return <InviteTeamMemberModalView {...viewProps} />;
};
